import { useEffect, useMemo, useRef, useState } from 'react';
import { IMPORT_ACCEPT } from '../../audio/assets/formats';
import * as audioOps from '../../domain/audioOps';
import { INSTRUMENT_LABELS } from '../../domain/factories';
import { MAX_AUDIO_ASSETS, MAX_PROJECT_AUDIO_BYTES, MAX_SAMPLE_BYTES, MAX_SAMPLE_SECONDS, type Asset } from '../../domain/schemas';
import { useObservable } from '../../lib/useObservable';
import { useProject } from '../../state/projectContext';
import { useServices } from '../../state/services';
import { useOpRunner } from '../music/useOpRunner';
import { Alert, Button, ConfirmDialog, Panel, Spinner, inputClass } from '../ui';
import { formatSeconds } from './format';
import type { LibraryActions } from './useAudioLibrary';

type Filter = 'all' | 'imported' | 'recorded' | 'unused';

const mb = (bytes: number) => `${(bytes / 1048576).toFixed(bytes < 1048576 ? 2 : 1)} MB`;

export function SampleLibrary({ library }: { library: LibraryActions }) {
  const { project } = useProject();
  const { selection, repo } = useServices();
  const selected = useObservable(selection);
  const { apply, error: opError } = useOpRunner();
  const input = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState<Asset | null>(null);
  const [preview, setPreview] = useState<{ id: string; url: string } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const audioEl = useRef<HTMLAudioElement>(null);

  const assets = project.assets.filter((a) => a.kind === 'audio');
  const totalBytes = audioOps.projectAudioBytes(project);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return assets.filter((a) => {
      if (q && !a.name.toLowerCase().includes(q)) return false;
      if (filter === 'imported') return a.source === 'local-file';
      if (filter === 'recorded') return a.source === 'recording';
      if (filter === 'unused') return !audioOps.isAssetUsed(project, a.id);
      return true;
    });
  }, [assets, filter, project, query]);

  // Preview: plays the stored file with a plain <audio> element (no decoding into memory).
  const stopPreview = () => {
    audioEl.current?.pause();
    setPreview((p) => {
      if (p) URL.revokeObjectURL(p.url);
      return null;
    });
  };
  useEffect(() => () => stopPreview(), []);
  useEffect(() => {
    if (preview && audioEl.current) {
      audioEl.current.src = preview.url;
      audioEl.current.play().catch(() => {
        setPreviewError('This browser cannot play that file.');
        stopPreview();
      });
    }
  }, [preview]);
  const togglePreview = async (asset: Asset) => {
    setPreviewError(null);
    if (preview?.id === asset.id) {
      stopPreview();
      return;
    }
    stopPreview();
    const blob = await repo.getBlob(asset.id).catch(() => undefined);
    if (!blob) {
      setPreviewError(`The data for “${asset.name}” is missing from this browser’s storage.`);
      return;
    }
    setPreview({ id: asset.id, url: URL.createObjectURL(new Blob([blob.data], { type: blob.mimeType })) });
    if (!asset.audio) void library.analyse(asset.id);
  };

  const usageText = (asset: Asset) => {
    const usage = audioOps.assetUsage(project, asset.id);
    const parts = [
      ...usage.instruments.map((i) => `${INSTRUMENT_LABELS[i]} sample`),
      ...(usage.clipIds.length ? [`${usage.clipIds.length} clip${usage.clipIds.length > 1 ? 's' : ''}`] : []),
    ];
    return parts.join(', ');
  };

  return (
    <Panel
      title="Sample library"
      actions={
        <>
          <span className="text-xs text-zinc-400" data-testid="library-usage">
            {assets.length}/{MAX_AUDIO_ASSETS} files · {mb(totalBytes)} of {mb(MAX_PROJECT_AUDIO_BYTES)}
          </span>
          <Button variant="primary" onClick={() => input.current?.click()} disabled={library.busy}>
            Import audio…
          </Button>
          <input
            ref={input}
            type="file"
            multiple
            accept={IMPORT_ACCEPT}
            className="sr-only"
            tabIndex={-1}
            aria-label="Import audio files"
            data-testid="import-audio"
            onChange={(e) => {
              const files = [...(e.target.files ?? [])];
              e.target.value = '';
              void library.importFiles(files).then((ids) => {
                if (ids.length) selection.selectSample(ids[ids.length - 1]!);
              });
            }}
          />
        </>
      }
    >
      <p className="mb-3 text-xs text-zinc-400">
        WAV, plus MP3/OGG when this browser can decode them · up to {MAX_SAMPLE_BYTES / 1048576} MB and {MAX_SAMPLE_SECONDS / 60} min per file. Files stay in this
        browser (IndexedDB) and are never uploaded; edits never change the original file.
      </p>
      <div className="mb-3 flex flex-wrap gap-2">
        <label htmlFor="sample-search" className="sr-only">
          Search samples
        </label>
        <input id="sample-search" type="search" placeholder="Search samples" value={query} onChange={(e) => setQuery(e.target.value)} className={`${inputClass} max-w-60`} />
        <label htmlFor="sample-filter" className="sr-only">
          Filter samples
        </label>
        <select id="sample-filter" value={filter} onChange={(e) => setFilter(e.target.value as Filter)} className="rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm">
          <option value="all">All</option>
          <option value="imported">Imported</option>
          <option value="recorded">Recorded</option>
          <option value="unused">Unused</option>
        </select>
      </div>
      <div aria-live="polite" className="flex flex-col gap-2">
        {library.busy && <Spinner label="Decoding and saving…" />}
        {library.error && (
          <Alert title={library.error} action={<Button size="sm" variant="ghost" onClick={library.clearError}>Dismiss</Button>}>
            {library.errorDetails.length > 0 && <ul className="list-disc pl-4">{library.errorDetails.map((d) => <li key={d}>{d}</li>)}</ul>}
          </Alert>
        )}
        {previewError && <Alert tone="warning" title={previewError} />}
        {opError && <Alert title={opError} />}
      </div>
      {/* Preview player (user audio: no captions available). */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioEl} className="hidden" onEnded={stopPreview} />
      {assets.length === 0 ? (
        <p className="mt-2 text-sm text-zinc-400">No samples yet. Import a file or record one below.</p>
      ) : shown.length === 0 ? (
        <p className="mt-2 text-sm text-zinc-400">No samples match.</p>
      ) : (
        <ul className="mt-2 flex flex-col gap-1.5" aria-label="Samples" data-testid="sample-list">
          {shown.map((asset) => {
            const isSelected = selected.sampleId === asset.id;
            const usage = usageText(asset);
            return (
              <li
                key={asset.id}
                className={`flex flex-col gap-2 rounded-md border p-2.5 sm:flex-row sm:items-center sm:justify-between ${
                  isSelected ? 'border-accent-400 bg-accent-600/10' : 'border-zinc-800 bg-zinc-800/50'
                }`}
                data-testid="sample-row"
              >
                <div className="min-w-0 flex-1">
                  {renaming?.id === asset.id ? (
                    <form
                      className="flex gap-2"
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (apply((p) => audioOps.renameAsset(p, asset.id, renaming.name))) setRenaming(null);
                      }}
                    >
                      <label htmlFor={`rename-${asset.id}`} className="sr-only">
                        New name for {asset.name}
                      </label>
                      <input
                        id={`rename-${asset.id}`}
                        ref={(el) => el?.focus()}
                        value={renaming.name}
                        maxLength={200}
                        onChange={(e) => setRenaming({ id: asset.id, name: e.target.value })}
                        onKeyDown={(e) => e.key === 'Escape' && setRenaming(null)}
                        className={inputClass}
                      />
                      <Button type="submit" size="sm" variant="primary">
                        Save
                      </Button>
                    </form>
                  ) : (
                    <button
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => selection.selectSample(isSelected ? null : asset.id)}
                      className="block w-full truncate text-left text-sm font-medium"
                      data-testid="sample-select"
                    >
                      {asset.name}
                    </button>
                  )}
                  <p className="text-xs text-zinc-400">
                    {asset.source === 'recording' ? 'Recording' : 'Imported'} · {mb(asset.sizeBytes)} · {asset.mimeType.split('/')[1]?.toUpperCase()}
                    {asset.audio
                      ? ` · ${formatSeconds(asset.audio.durationSec)} · ${asset.audio.channels === 1 ? 'mono' : asset.audio.channels === 2 ? 'stereo' : `${asset.audio.channels} ch`}`
                      : ' · not analysed yet'}
                    {usage && <span className="text-accent-300"> · used: {usage}</span>}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  <Button size="sm" onClick={() => void togglePreview(asset)} aria-pressed={preview?.id === asset.id} data-testid="sample-preview">
                    {preview?.id === asset.id ? '■ Stop' : '▶ Preview'}
                    <span className="sr-only"> {asset.name}</span>
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setRenaming({ id: asset.id, name: asset.name })}>
                    Rename<span className="sr-only"> {asset.name}</span>
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-300" onClick={() => setDeleting(asset)} disabled={library.busy}>
                    Delete<span className="sr-only"> {asset.name}</span>
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <ConfirmDialog
        open={deleting !== null}
        danger
        title={`Delete “${deleting?.name ?? ''}”?`}
        body={
          deleting && audioOps.isAssetUsed(project, deleting.id) ? (
            <>
              This file is in use ({usageText(deleting)}). Deleting it also removes those clips and returns those tracks to their built-in sounds. This cannot be
              undone.
            </>
          ) : (
            'The file is removed from this project and from browser storage. This cannot be undone.'
          )
        }
        confirmLabel={deleting && audioOps.isAssetUsed(project, deleting.id) ? 'Delete file and its uses' : 'Delete file'}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          const asset = deleting!;
          setDeleting(null);
          if (preview?.id === asset.id) stopPreview();
          void library.remove(asset.id, true).then((ok) => {
            if (ok && selection.get().sampleId === asset.id) selection.selectSample(null);
            const clip = selection.get().clipId;
            if (ok && clip && !project.audioClips.some((c) => c.id === clip && c.assetId !== asset.id)) selection.selectClip(null);
          });
        }}
      />
    </Panel>
  );
}
